import React, { useEffect } from "react";
import Footer from "../components/Footer";

const DotLeader = () => (
  <div className="flex-1 border-b border-dotted border-gray-300 mx-3 mb-1" />
);

const MenuItem = ({ name, subName, note, price, callNote }) => (
  <div className="py-2.5">
    <div className="flex items-end">
      <div className="flex-1 min-w-0 mr-2">
        <span className="font-semibold text-gray-900">
          {name}
        </span>
        {subName && (
          <span className="text-gray-500 text-sm ml-2 italic">
            {subName}
          </span>
        )}
      </div>
      {price && <DotLeader />}
      {price && (
        <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
          {price}
        </span>
      )}
    </div>
    {note && (
      <p className="text-sm text-gray-500 italic mt-0.5">
        {note}
      </p>
    )}
    {callNote && (
      <p className="text-xs text-amber-700 font-medium mt-1">
        {callNote}
      </p>
    )}
  </div>
);

const DayBadge = ({ day }) => (
  <span className="inline-block text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full mb-1.5">
    {day}
  </span>
);

const SectionCard = ({ icon, title, accentClass, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className={`px-6 py-5 ${accentClass}`}>
      <h2 className="text-xl font-bold text-white tracking-wide flex items-center gap-2">
        {icon && <span className="text-2xl">{icon}</span>}
        {title}
      </h2>
    </div>
    <div className="px-6 py-2 divide-y divide-gray-100">
      {children}
    </div>
  </div>
);

function MenuPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const restaurantData = {
    phone: "973-350-6522",
    name: "A Plus Flavor",
    address: "677 Bergen St., Newark, NJ 07108",
    hours: "Tue-Sun: 10AM-8PM | Mon: Closed",
    socialLinks: {
      facebook:
        "https://www.facebook.com/profile.php?id=61576392401010",
      instagram:
        "https://www.instagram.com/aplusflavorrestaurant",
    },
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div
        className="flex-1"
        style={{
          background:
            "linear-gradient(160deg, #faf8f5 0%, #ede8df 100%)",
        }}
      >
        {/* Page Header */}
        <div className="bg-stone-900 text-white py-14 px-4 text-center">
          <p className="text-amber-400 text-xs font-bold tracking-[0.25em] uppercase mb-3">
            A Plus Flavor
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Our Menu
          </h1>
          <div className="mt-4 mx-auto w-16 h-0.5 bg-amber-400 rounded-full" />
        </div>

        <div className="max-w-3xl mx-auto px-4 py-12 space-y-8">
          {/* ── Breakfast ── */}
          <SectionCard
            icon="🍳"
            title="Breakfast Plates"
            accentClass="bg-stone-800"
          >
            {/* Combo highlight */}
            <div className="py-3">
              <DayBadge day="⭐ Breakfast Combo" />
              <div className="flex items-end">
                <div className="flex-1 min-w-0 mr-2">
                  <span className="font-semibold text-gray-900">
                    Bacon, Ham or Turkey with Scrambled Eggs
                    &amp; Cheese on a Roll
                  </span>
                  <p className="text-sm text-gray-500 italic">
                    with Coffee or Tea
                  </p>
                </div>
                <DotLeader />
                <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                  $6.00
                </span>
              </div>
            </div>
            <MenuItem
              name="Spaghetti Haitien"
              price="$10.00"
              note="Haitian-style spaghetti with hot dogs, onions, peppers, and a light tomato sauce"
            />
            <MenuItem
              name="Mayi Moulen"
              subName="Cornmeal"
              price="$10.00"
            />
          </SectionCard>

          {/* ── Lunch Specials ── */}
          <SectionCard
            icon="☀️"
            title="Lunch Specials"
            accentClass="bg-amber-700"
          >
            <div className="pt-3 pb-1">
              <p className="text-sm font-semibold text-amber-700 mb-3 tracking-wide">
                Available 12 PM – 3 PM
              </p>
            </div>
            <MenuItem
              name="Chicken + Plantains"
              price="$10.00"
            />
            <MenuItem
              name="Crispy Griot Bites + French Fries"
              price="$10.00"
            />
          </SectionCard>

          {/* ── Main Dishes ── */}
          <SectionCard
            icon="🍽️"
            title="Main Dishes"
            accentClass="bg-stone-800"
          >
            <div className="pt-4 pb-2">
              <div className="flex flex-wrap gap-2 mb-1">
                <span className="text-sm bg-stone-100 text-stone-700 px-3 py-1 rounded-full font-semibold">
                  Medium — $15.00
                </span>
                <span className="text-sm bg-stone-100 text-stone-700 px-3 py-1 rounded-full font-semibold">
                  Large Complete — $20.00
                </span>
              </div>
              <p className="text-xs text-gray-400 italic mt-1">
                Served with Rice, Plantains &amp; Pikliz
              </p>
            </div>
            <MenuItem
              name="Poulet Frite / Poulet en Sauce / Jerk Chicken"
              note="Fried Chicken or Stewed Chicken"
            />
            <MenuItem
              name="Legim"
              subName="Vegetable Stew with Meat"
            />
            <MenuItem
              name="Fritaille"
              subName="Fried meat with Plantains, Accra &amp; Pikliz"
              price="$25.00"
            />
            <MenuItem name="Griot" subName="Fried Pork" />
            <MenuItem
              name="Tassot Kabrit"
              subName="Fried Goat"
            />
            <MenuItem name="Tassot Bef" subName="Fried Beef" />
            <MenuItem
              name="Poisson"
              subName="Whole Fried Fish"
              price="$30.00"
              callNote="Please call ahead to place your order"
            />
          </SectionCard>

          {/* ── Daily Specials ── */}
          <SectionCard
            icon="🍲"
            title="Daily Specials"
            accentClass="bg-red-900"
          >
            {/* Friday */}
            <div className="py-2.5">
              <DayBadge day="Friday" />
              <div className="flex items-end">
                <div className="flex-1 min-w-0 mr-2">
                  <span className="font-semibold text-gray-900">
                    Ragout Beef
                  </span>
                  <p className="text-sm text-gray-500 italic">
                    Stewed cow foot
                  </p>
                </div>
                <DotLeader />
                <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                  $12.00
                </span>
              </div>
              <div className="flex items-end mt-2">
                <div className="flex-1 min-w-0 mr-2">
                  <span className="font-semibold text-gray-900">
                    Soup Bouillon
                  </span>
                </div>
                <DotLeader />
                <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                  $10.00
                </span>
              </div>
            </div>

            {/* Diri Djon Djon */}
            <div className="py-2.5">
              <DayBadge day="Diri Djon Djon" />
              <MenuItem name="Plain" price="$12.00" />
              <MenuItem name="With Chicken" price="$15.00" />
              <MenuItem
                name="With Stew Vegetables"
                price="$15.00"
              />
            </div>

            {/* Saturday */}
            <div className="py-2.5">
              <DayBadge day="Saturday" />
              <div className="space-y-2">
                <div className="flex items-end">
                  <div className="flex-1 min-w-0 mr-2">
                    <span className="font-semibold text-gray-900">
                      Tonmtonm ak Sos Kalalou
                    </span>
                  </div>
                  <DotLeader />
                  <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                    $15.00
                  </span>
                </div>
                <div className="flex items-end">
                  <div className="flex-1 min-w-0 mr-2">
                    <span className="font-semibold text-gray-900">
                      Bouillon
                    </span>
                    <p className="text-sm text-gray-500 italic">
                      Beef &amp; Vegetable Soup
                    </p>
                  </div>
                  <DotLeader />
                  <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                    $12.00
                  </span>
                </div>
              </div>
            </div>

            {/* Sunday */}
            <div className="py-2.5">
              <DayBadge day="Sunday" />
              <div className="flex items-end">
                <div className="flex-1 min-w-0 mr-2">
                  <span className="font-semibold text-gray-900">
                    Soup Joumou
                  </span>
                  <p className="text-sm text-gray-500 italic">
                    Traditional Haitian pumpkin soup with beef
                    &amp; vegetables
                  </p>
                </div>
                <DotLeader />
                <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                  $12.00
                </span>
              </div>
            </div>

            {/* All week */}
            <div className="py-2.5">
              <div className="flex items-end">
                <div className="flex-1 min-w-0 mr-2">
                  <span className="font-semibold text-gray-900">
                    Fried Chicken / Baked Turkey Wings
                  </span>
                  <p className="text-sm text-gray-500 italic">
                    Served with Collard Greens and Mac &amp;
                    Cheese
                  </p>
                </div>
                <DotLeader />
                <span className="font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                  $20.00
                </span>
              </div>
            </div>
          </SectionCard>

          {/* ── Drinks ── */}
          <SectionCard
            icon="🥤"
            title="Drinks"
            accentClass="bg-stone-800"
          >
            <MenuItem name="Coffee / Tea" price="$2.00" />
            <MenuItem name="Bottle of Water" price="$1.00" />
            <MenuItem name="Ginger Drink" price="$5.00" />
            <MenuItem name="Acassan Drink" price="$5.00" />
            <MenuItem
              name="Fresh Juice"
              price="$4.00"
              note="Lemonade, Carrot, Beet Juice, Passion Fruit"
            />
          </SectionCard>

          {/* ── Haitian Patties ── */}
          <SectionCard
            icon="🥟"
            title="Haitian Patties"
            accentClass="bg-stone-800"
          >
            <MenuItem name="Haitian Patties" price="$2.00" />
          </SectionCard>
        </div>
      </div>

      <Footer restaurantData={restaurantData} />
    </div>
  );
}

export default MenuPage;
